import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import {
    // eslint-disable-next-line no-unused-vars
    DateField, Icon, Loader, Accordion
} from '@jutro/components';
import { TranslatorContext, IntlContext, withIntl } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useDependencies } from 'gw-portals-dependency-react';
import { isCapabilityEnabled, getNormalizedLOBName } from 'gw-portals-config-js';
import { useAuthentication } from 'gw-digital-auth-react';
import { PolicyService } from 'gw-capability-policy';
import { DocumentService } from 'gw-capability-policydocument';
import { BillingService } from 'gw-capability-billing';
import {
    PolicyCommonDetails,
    PolicyCommonDocuments,
    PolicyCommonContacts,
    PolicyCoverages
} from 'gw-capability-policy-common-react';
import {Link as HyperLink, Link} from "gw-components-platform-react";
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import metadata from './PolicyDetails.metadata.json5';
import messages from './PolicyDetails.messages';
import styles from './PolicyDetails.module.scss';
import PolicyLiabilityComponent
    from '../../nexus-capability-policy-common-react/components/PolicyLiabilityComponent/PolicyLiabilityComponent';
import PolicyLineLevelCoveragesComponent
    from '../../nexus-capability-policy-common-react/components/CP/PolicyLineLevelCoveragesComponent/PolicyLineLevelCoveragesComponent';
import PolicyFarmInformationComponent
    from '../../nexus-capability-policy-common-react/components/Farm/PolicyFarmInformationComponent/PolicyFarmInformationComponent';
import PolicyScheduleItemsComponent
    from '../../nexus-capability-policy-common-react/components/PolicyScheduleItemsComponent/PolicyScheduleItemsComponent';
import AccountDocumentSummary from '../../nexus-capability-policy-common-react/components/PolicyCommonDocuments/AccountDocumentSummary/AccountDocumentSummary';
import PageBannerComponent
    from '../../../../components/PageBannerComponent/PageBannerComponent';
import claimDetailsStyles from "../../nexus-capability-claim-react/pages/ClaimDetails/ClaimDetails.module.scss";
import { ServiceManager } from '@jutro/services';
import { PhoneUtil } from 'gw-portals-viewmodel-js';


const handlePrint = () => {
    window.print();
};

function PolicyDetails(props) {
    const { history, match, location: { state } } = props;
    const { authHeader } = useAuthentication();
    const [policyCommonDetails, updatePolicyCommonDetails] = useState({
        currentPeriodDetails: {},
        renewedPeriodDetails: {},
        notesArray: {},
        policyBillingSummary: {},
        hasRenewedPeriod: false
    });
    const [writableDocuments, updateWritableDocuments] = useState([]);
    const [policyBillingSummary, updatePolicyBillingSummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const { capabilitiesConfig, messageToBrokerEnabled } = appConfig;
    const policyNumber = _.get(match, 'params.policyNumber');
    const isBroker = _.get(policyCommonDetails.currentPeriodDetails, 'producerContact.isBroker');
    let selectedTab = _.get(state, 'selectedTab', 'currentPeriod');

    let pageRef = React.createRef();
    const [pageLoaded, setPageLoaded] = useState(false);

    useEffect(
        () => {
            try {
                getPolicyDetails();
            } catch (e) {
                throw new Error(e);
            }
            window.scrollTo(0, 0);

            if (!pageLoaded) {
                const element = ReactDOM.findDOMNode(pageRef.current).parentNode;
                window.setTimeout(() => {
                    element.children.policyDetails.focus();
                }, 1000);
                setPageLoaded(true);
            }
        },
        [writableDocuments.length]
    );

    const getBackButton = () => {
        const breakpoint = useContext(BreakpointTrackerContext);
        return breakpoint === 'desktop' ? 'Back' : '';
    };

    const getPolicyDetails = useCallback(async () => {
        const policyDetails = await PolicyService.getAccountPolicyDetails(policyNumber, authHeader);
        //~Add message load
        const policyNotes = null;
        // await PolicyService.getNotesForPolicy(policyNumber, authHeader);

        if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' })) {
            const _policyBillingSummary = await BillingService.getPaymentSummaryForPolicy_EY(policyNumber, authHeader);
            updatePolicyBillingSummary(_policyBillingSummary);
            policyDetails.currentPeriod.policyBillingSummary = _policyBillingSummary.currentPeriod;
            if (policyDetails.renewedPeriod) {
                policyDetails.renewedPeriod.policyBillingSummary = _policyBillingSummary.renewedPeriod;
            }
        }

        updatePolicyCommonDetails({
            currentPeriodDetails: policyDetails.currentPeriod,
            renewedPeriodDetails: policyDetails.renewedPeriod ? policyDetails.renewedPeriod : {},
            notesArray: policyNotes ? policyNotes.map((note) => {
                return {
                    author: note.authorName,
                    date: note.createdDate,
                    subject: note.subject,
                    note: note.body
                };
            }) : {},
            hasRenewedPeriod: !_.isEmpty(policyDetails.renewedPeriod),
            policyBillingSummary: policyBillingSummary
        });

        setLoading(false);
    }, [policyNumber, authHeader]);

    const redirectToEndorsement = useCallback(
        (policyDetails, requestType) => {
            const productCode = _.findKey(policyDetails.lobs, (lob) => !_.isEmpty(lob));
            history.push(`/endorsement-${getNormalizedLOBName(productCode)}`,
                { policyNumber, policyDetails, requestType });
        },
        [history, policyNumber]
    );

    const canChangePolicy = useCallback(
        (policyPeriod) => {
            if (!_.isEmpty(policyPeriod)) {
                const productCode = _.findKey(policyPeriod.lobs, (lob) => !_.isEmpty(lob));
                if (_.isEmpty(productCode)) {
                    return false;
                }
                return (
                    isCapabilityEnabled({
                        capabilitiesConfig,
                        capabilityName: 'policychange',
                        lob: productCode
                    }) && policyPeriod.canBeChanged
                );
            }
            return false;
        },
        [capabilitiesConfig]
    );

    const getPolicyDownloadLink = useCallback(() => {
        return DocumentService.downloadPolicyDocument(
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardPublicID'),
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardSessionID')
        );
    }, [policyCommonDetails]);


    const getCovCardHeaderValue = (polCommonDetails) => {
        const { productName } = polCommonDetails.currentPeriodDetails;
        let headerValue = '';
        switch (productName) {
            case 'Personal Auto':
            case 'Commercial Auto':
            case 'Fleet':
            case 'Auto':
                headerValue = translator(messages.autoCovCardHeader);
                break;
            case 'Commercial Property':
                headerValue = translator(messages.propCovCardHeader);
                break;
            case 'Residential':
                headerValue = translator(messages.propCovCardHeader);
                break;
            case 'Marine':
                headerValue = translator(messages.boatCovCardHeader);
                break;
            case 'Umbrella':
                headerValue = translator(messages.umbrellaCovCardHeader);
                break;
            case 'Farm':
                headerValue = translator(messages.farmCovCardHeader);
                break;
            default:
                headerValue = 'ADD THE CODE';
        }

        //    console.log('headerValue ', headerValue);
        return headerValue;
    };

    const getPagedHeaderValue = (polCommonDetails) => {
        const headerValue = `${polCommonDetails.currentPeriodDetails.productName} Policy: ${polCommonDetails.currentPeriodDetails.policyNumber}`;
        //   console.log('pageheaderValue ', headerValue);
        return headerValue;
    };

    const getAgentTitle = useCallback(() => {
        //let isBroker= _.get(policyCommonDetails.currentPeriodDetails, 'producerContact.isBroker')
        const stateCode = _.get(policyCommonDetails.currentPeriodDetails, 'policyAddress.state')
        if (stateCode === 'ON') {
            if (isBroker) {
                return translator(messages.myBroker);
            }
            return translator(messages.myAdvisor);
        } else {
            if (isBroker) {
                return translator(messages.myBroker);
            }
            return translator(messages.mySales);
        }
    }, [translator, policyCommonDetails]);

    const getAgentName = useCallback(() => {
        let agentName = _.get(policyCommonDetails.currentPeriodDetails, 'producerContact.displayName')
        //  console.log("agentName: ", agentName)
        if (_.isEmpty(agentName)) {
            return translator(messages.na)
            //return "EY - Internal Code"
        }
        return agentName;
        //return "EY - Internal Code";
    }, [translator, policyCommonDetails]);

    const getAgentPhone = useCallback(() => {
        let phoneNumber = _.get(policyCommonDetails.currentPeriodDetails, 'producerContact.phoneNumber');
        const localeService = ServiceManager.getService('locale-service');
        const phoneUtil = PhoneUtil();
        const agentPhone =  phoneUtil.prettyPrint(phoneNumber, localeService.getDefaultCountryCode());
        if (_.isEmpty(agentPhone)) {
            return translator(messages.na)
        }
        return (
            <Link aria-label="Agent phone number" className={styles.customLink} onClick={(e) => phoneToTrigger(e, agentPhone)} href="/">
                {agentPhone}
            </Link>
        )
    }, [policyCommonDetails]);

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const phoneToTrigger = useCallback((event, phone) => {
        event.preventDefault();
        window.location = `tel:${phone}`;
    }, []);

    const getAgentEmail = useCallback(() => {
        const agentEmail = _.get(policyCommonDetails.currentPeriodDetails, 'producerContact.email');
        if (_.isEmpty(agentEmail)) {
            return translator(messages.na);
        }

        return (
            <Link className={styles.customLink} onClick={(e) => mailToTrigger(e, agentEmail)} href="/">
                {agentEmail}
            </Link>
        );
    }, [policyCommonDetails]);

    const showLiability = (polCommonDetails) => {
        const resultValue = (polCommonDetails.currentPeriodDetails.productName !== 'Personal Auto'
            && !_.isEmpty(policyCommonDetails.currentPeriodDetails)
            && _.isEmpty(policyCommonDetails.currentPeriodDetails.lobs.personalAuto)
            && !_.isEmpty(policyCommonDetails.currentPeriodDetails.coverables));
        //  console.log('resultValue ', resultValue);
        return resultValue;
    };

    const addNote = () => {
        const {
            history,
            match: {
                params: { policyNumber }
            }
        } = props;
        // const { redirectPath } = state;
        const nextPath = `/account-policy-details/${policyNumber}/create-note`;
        history.push({
            pathname: nextPath,
            state: {
                claimNumber: policyNumber,
                redirectPath: `/account-policy-details/${policyNumber}`
            }
        });
    };

    const getNotesColumnCell = (item, index, { path: property }) => {
        //const translator = context;
        let toolTipMessage = '';
        switch (property) {
            case 'author':
                toolTipMessage = translator(messages.notesAuthor);
                break;
            case 'date':
                toolTipMessage = translator(messages.notesDate);
                break;
            case 'subject':
                toolTipMessage = translator(messages.notesSubject);
                break;
            case 'note':
                toolTipMessage = translator(messages.notesContent);
                break;
            default:
                toolTipMessage = '';
        }
        return (
            <div
            >
                <span title={toolTipMessage}>{item[property]}</span>
            </div>
        );
    };

    const getNotesArray = (notes) => {

        return !_.isEmpty(notes) ? notes.map((noteData) => {
            const noteDate = intl.formatDate(new Date(noteData.date), { year: 'numeric', month: 'short', day: 'numeric' });
            return {
                author: noteData.author,
                date: `${noteDate} ${formatAMPM(new Date(noteData.date))}`,
                subject: noteData.subject,
                note: noteData.note
            };
        }) : null;
    };

    const formatAMPM = (date) => {
        let hours = date.getHours() % 12;
        let minutes = date.getMinutes();
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
        hours = hours || 12;
        minutes = minutes < 10 ? `0${minutes}` : minutes;
        const strTime = `${hours}:${minutes} ${ampm}`;
        return strTime;
    };

    const getEmptyMessageVisibility = (notes) => {
        if (notes.length === 0) {
            return true;
        }
        return false;
    };

    const getNotesTableVisibility = (notes) => {
        if (notes.length > 0) {
            return true;
        }
        return false;
    };

    const showPolicyLineCoverages = (polCommonDetails) => {
        const resultValue = (polCommonDetails.currentPeriodDetails.productName === 'Commercial Property'
            || polCommonDetails.currentPeriodDetails.productName === 'Residential'
            || polCommonDetails.currentPeriodDetails.productName === 'Farm');
        //   console.log('showPolicyLineCoverages ', resultValue);
        return resultValue;
    };

    let scheduleItemsPath = "";
    //scheduleItemsPath = getScheduleItemCoverables(policyCommonDetails);
    let scheduleItemVisible = false;
    let lineLevelCoverageVisible = false;
    let liabilityCoverageVisible = false;
    let farmInfoVisible = false;
    let policyNotesArray = [];

    const getScheduleItemCoverables = (polCommonDetails) => {
        const { productName } = polCommonDetails.currentPeriodDetails;
        switch (productName) {
            case 'Residential':
                //  scheduleItemsPath = "currentPeriodDetails.coverables";
                scheduleItemVisible = true;
                lineLevelCoverageVisible = false;
                liabilityCoverageVisible = true;
                break;
            case 'Commercial Property':
                lineLevelCoverageVisible = false;
                liabilityCoverageVisible = true;
                break;
            case 'Farm':
                liabilityCoverageVisible = true;
                farmInfoVisible = true;
                scheduleItemVisible = false;
                break;
            case 'Marine':
                liabilityCoverageVisible = true;
                break;
            default:
                scheduleItemsPath = "";
                scheduleItemVisible = false;
                lineLevelCoverageVisible = false;
                liabilityCoverageVisible = false;
        }

        return scheduleItemVisible;
    };

    const overrideProps = {
        pageTitle: {
            content: getPagedHeaderValue(policyCommonDetails)
        },
        currentPeriod: {
            heading: translator(messages.currentTab)
        },
        renewalPeriod: {
            heading: policyCommonDetails.hasRenewedPeriod ? translator(messages.renewalTab) : '',
            disabled: !policyCommonDetails.hasRenewedPeriod,
            visible: policyCommonDetails.hasRenewedPeriod
        },
        policyChangeButtonContainer: {
            visible: canChangePolicy(
                policyCommonDetails.currentPeriodDetails,
                policyCommonDetails.currentPeriodDetails.productName
            )
        },
        renewalPolicyChangeButtonContainer: {
            visible: canChangePolicy(
                policyCommonDetails.renewedPeriodDetails,
                policyCommonDetails.currentPeriodDetails.productName
            )
        },
        idCardContainer: {
            visible: !_.isEmpty(_.get(policyCommonDetails.currentPeriodDetails, 'idCardPublicID'))
        },
        idCard: {
            href: getPolicyDownloadLink(),
            target: '_blank'
        },
        policyDocumentsSection: {
            updateWritableDocuments: updateWritableDocuments,
            writableDocuments: writableDocuments,
            showFileUpload: true,
            layout: ['1', '1', '1']
        },
        covCard: {
            header: getCovCardHeaderValue(policyCommonDetails)
        },
        accordionEmptyMsg: {
            visible: policyCommonDetails.currentPeriodDetails && policyCommonDetails.currentPeriodDetails.lobs
                && _.isEmpty(policyCommonDetails.currentPeriodDetails.lobs.homeOwners) && policyCommonDetails.currentPeriodDetails.productName === 'Farm'
        },
        emptyMsg: {
            visible: policyCommonDetails.currentPeriodDetails && policyCommonDetails.currentPeriodDetails.lobs
                && _.isEmpty(policyCommonDetails.currentPeriodDetails.lobs.homeOwners) && policyCommonDetails.currentPeriodDetails.productName === 'Farm'
        },
        renewalCovCard: {
            header: getCovCardHeaderValue(policyCommonDetails)
        },
        // scheduleItemsSection: {
        //     path: getScheduleItemCoverables(policyCommonDetails)
        // },
        scheduleCard: {
            visible: getScheduleItemCoverables(policyCommonDetails)//scheduleItemVisible
        },
        liabilityCovCard: {
            visible: liabilityCoverageVisible
        },
        farmInfoCovCard: {
            visible: farmInfoVisible
        },
        policyLineLevelCovCard: {
            visible: lineLevelCoverageVisible,
            header: policyCommonDetails.currentPeriodDetails.productName == 'Commercial Property'
            ? translator(messages.commercialCovCardHeader) : translator(messages.commonCovCardHeader)
        },
        agentName: {
            content: getAgentName()
        },
        agentPhone: {
            content: getAgentPhone()
        },
        agentEmail: {
            content: getAgentEmail()
        },
        lineLevelCovCard: {
            visible: showPolicyLineCoverages(policyCommonDetails)
        },
        myAgentTableTitle: {
            content: getAgentTitle()
        },
        notesTableGrid: {
            data: getNotesArray(policyCommonDetails.notesArray),
            visible: getNotesTableVisibility(policyCommonDetails.notesArray)
        },
        emptyNotesMessage: {
            visible: getEmptyMessageVisibility(policyCommonDetails.notesArray)
        },
        /* sendMessageGrid: {
            visible: !isBroker
        }, */
        notesTab: {
            visible: !isBroker
        },
        policyPeriodTabSet: {
            defaultActiveTab: selectedTab
        },
        backButton: {
            content: getBackButton()
        },
        pageHeaderBanner: {
            pageTitle: `${policyCommonDetails.currentPeriodDetails.productName} Policy #: ${policyCommonDetails.currentPeriodDetails.policyNumber}`,
            lobCode: policyCommonDetails.currentPeriodDetails.productName
        }
    };



    const resolvers = {
        resolveComponentMap: {
            policycommondetailscomponent: PolicyCommonDetails,
            policycoveragescomponent: PolicyCoverages,
            policyliabilitycomponent: PolicyLiabilityComponent,
            //  policycommondocumentscomponent: PolicyCommonDocuments,
            policycommondocumentscomponent: AccountDocumentSummary,
            policyLineLevelCoveragescomponent: PolicyLineLevelCoveragesComponent,
            policycommoncontactscomponent: PolicyCommonContacts,
            policyFarmInformationComponent: PolicyFarmInformationComponent,
            policyScheduleItemsComponent: PolicyScheduleItemsComponent,
            pagebannercomponent: PageBannerComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onPrint: handlePrint,
            addNote: addNote,
            getNotesColumnCell: getNotesColumnCell,
            onChangePolicy: () => redirectToEndorsement(policyCommonDetails.currentPeriodDetails, 'policyChange'),
            onChangePolicyRenewal: () => redirectToEndorsement(
                policyCommonDetails.renewedPeriodDetails, 'policyRenew'
            )
        }
    };

    return (
        <div>
            <div id="policyDetails" role="navigation" title="Landed Policy Details Page" tabindex="0" ref={pageRef} />
            <Loader loaded={!loading}>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={policyCommonDetails}
                    overrideProps={overrideProps}
                    componentMap={resolvers.resolveComponentMap}
                    classNameMap={resolvers.resolveClassNameMap}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </Loader>
        </div>
    );
}
PolicyDetails.propTypes = {
    match: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        productCode: PropTypes.string
    }).isRequired
};

export default withRouter(PolicyDetails);
